import { createContext, PropsWithChildren, useCallback, useContext, useState } from "react";

import { OrganizationRoutes } from "@simplyk/common";
import { useSearchParams } from "next/navigation";
import { useRouter } from "next/router";

import { EmailEditorDrawer } from "../components/EmailEditor/EmailEditorDrawer";

interface EmailEditorContextProps {
  openEmailEditorDrawer: (emailContentId: string, thankYouTransactionId?: string) => void;
  closeEmailEditorDrawer: () => void;
  getThankYouEmailFromCache: (transactionId: string) => string | undefined;
  saveThankYouEmailToCache: (transactionId: string, body: string) => void;
}

const EmailEditorContext = createContext<EmailEditorContextProps>({
  closeEmailEditorDrawer: () => {},
  openEmailEditorDrawer: () => {},
  getThankYouEmailFromCache: () => undefined,
  saveThankYouEmailToCache: () => {},
});

export const EmailEditorProvider = ({ children }: PropsWithChildren) => {
  const router = useRouter();
  const params = useSearchParams();
  const query = Object.fromEntries(params.entries());

  const emailContentId = params.get("emailContentId") as string;
  const thankYouTransactionId = params.get("thankYouTransactionId") as string;
  const pathname = router.pathname;
  const isBuilderFlow = pathname.includes(OrganizationRoutes.FullScreenEmailEditor);
  const isEmailEditorOpen = (Boolean(emailContentId) || Boolean(thankYouTransactionId)) && !isBuilderFlow;

  // Cache for AI-generated thank you emails, keyed by transactionId
  const [thankYouEmailCache, setThankYouEmailCache] = useState<Record<string, string>>({});

  const getThankYouEmailFromCache = useCallback(
    (transactionId: string) => {
      return thankYouEmailCache[transactionId];
    },
    [thankYouEmailCache]
  );

  const saveThankYouEmailToCache = useCallback((transactionId: string, body: string) => {
    setThankYouEmailCache((prev) => ({ ...prev, [transactionId]: body }));
  }, []);

  const openEmailEditorDrawer = useCallback(
    async (emailContentId: string, thankYouTransactionId?: string) => {
      router.push({
        query: { ...query, emailContentId, ...(thankYouTransactionId ? { thankYouTransactionId } : {}) },
      });
    },
    [query, router]
  );

  const closeEmailEditorDrawer = useCallback(() => {
    const { emailContentId: _emailContentId, thankYouTransactionId: _thankYouTransactionId, ...newQuery } = query;
    router.push({ query: newQuery });
  }, [query, router]);

  return (
    <EmailEditorContext.Provider
      value={{ openEmailEditorDrawer, closeEmailEditorDrawer, getThankYouEmailFromCache, saveThankYouEmailToCache }}
    >
      {children}
      <EmailEditorDrawer
        emailContentId={emailContentId}
        closeEmailEditor={closeEmailEditorDrawer}
        isOpen={isEmailEditorOpen}
      />
    </EmailEditorContext.Provider>
  );
};

export const useEmailEditorContext = () => useContext(EmailEditorContext);
